// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a component for displaying a chronological list of audit events
 * related to a specific note.
 */
import { getNoteAuditEvents } from '@/audit/api/get-note-audit-events';
import { AuditEventListItem } from '@/audit/components/audit-event-list-item';
import { Stack } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';

interface Props {
  /**
   * The key of the note whose audit events should be displayed.
   */
  noteKey: string;
}

/**
 * Displays a vertical list of audit events for a specific note.
 * This component suspends while fetching the audit events.
 *
 * Each audit event is displayed using the AuditEventListItem component,
 * which provides a consistent format for displaying event details.
 */
export function NoteAuditList(props: Props) {
  const { data } = useSuspenseQuery({
    queryKey: ['note', props.noteKey, 'audit'],
    queryFn: () => getNoteAuditEvents(props.noteKey),
    retry: false,
  });

  return (
    <Stack spacing={2}>
      {data.events.map((event) => (
        <AuditEventListItem key={event.id} event={event} />
      ))}
    </Stack>
  );
}
